import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Session-scoped memo of the product template's section id, learned from the first full page
 * fetch. Every later quick add then asks for that section alone - on this shop that is ~180 KB
 * instead of the ~780 KB product page, roughly half of which is app embed script the modal
 * never uses. A product on a different template misses, gets a cheap 404, and relearns.
 */
const SECTION_ID_STORAGE_KEY = 'quick-add:section-id';

/** @returns {string | null} */
function readSectionId() {
  try {
    return sessionStorage.getItem(SECTION_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {string} sectionId */
function writeSectionId(sectionId) {
  try {
    sessionStorage.setItem(SECTION_ID_STORAGE_KEY, sectionId);
  } catch {
    // Storage disabled (private mode) - quick add just keeps fetching full pages.
  }
}

/**
 * @param {string} url
 * @param {string} sectionId
 * @returns {string}
 */
function withSectionId(url, sectionId) {
  const target = new URL(url, window.location.href);
  target.searchParams.set('section_id', sectionId);
  return target.href;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Element | null} Imported copy of the product page's [data-product-grid-content]. */
  #cachedProductGrid = null;

  /** @type {Promise<void> | null} In-flight fetch, so a click can await a running prefetch. */
  #pendingFetch = null;

  get cachedProductHtml() {
    return this.#cachedProductGrid;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    // product-card.js only prefetches the product page on pointerenter/focusin, and only when
    // isDesktopBreakpoint() is true - so on a phone the whole download happened after the tap,
    // with nothing on screen while it ran. Start it on the press that precedes the click.
    this.addEventListener('pointerdown', this.#prefetch, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerdown', this.#prefetch);
  }

  #prefetch = () => {
    if (this.#cachedProductGrid || this.#pendingFetch) return;

    this.fetchProductPage(this.productPageUrl)?.catch(() => {
      // A failed prefetch is not worth surfacing - the click retries and reports.
    });
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = this.#dialog;
    if (!dialogComponent) return;

    // Open first, fill second. The fetch below is a network round trip - a second on desktop
    // wifi, far longer on cellular - and the old order left the tap with no feedback at all
    // for its whole duration, which reads as a frozen page.
    const needsFetch = !this.#cachedProductGrid;
    if (needsFetch) this.#setLoading(dialogComponent, true);
    this.#openQuickAddModal();

    try {
      if (!this.#cachedProductGrid) await this.#ensureProductGrid();

      // Still nothing: the request failed, or a concurrent variant update aborted it. Closing
      // beats leaving an empty modal open, and the next click retries from scratch.
      if (!this.#cachedProductGrid) {
        this.#closeQuickAddModal();
        return;
      }

      await this.updateQuickAddModal(this.#freshProductGrid());
    } catch (error) {
      this.#closeQuickAddModal();
      throw error;
    } finally {
      this.#setLoading(dialogComponent, false);
    }
  };

  /** @returns {QuickAddDialog | null} */
  get #dialog() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    return dialogComponent instanceof QuickAddDialog ? dialogComponent : null;
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   * @param {boolean} loading
   */
  #setLoading(dialogComponent, loading) {
    dialogComponent.toggleAttribute('loading', loading);
    dialogComponent.setAttribute('aria-busy', String(loading));
  }

  /**
   * Awaits a prefetch that is already running before starting a request of its own.
   * @returns {Promise<void>}
   */
  async #ensureProductGrid() {
    if (this.#pendingFetch) {
      await this.#pendingFetch;
      if (this.#cachedProductGrid) return;
    }

    await this.fetchProductPage(this.productPageUrl);
  }

  /**
   * A throwaway copy of the cached section, so the modal can mutate it freely. Cloning a live
   * node costs nothing next to the old path, which serialised the entire product document to a
   * string and reparsed it on every single open.
   * @returns {DocumentFragment}
   */
  #freshProductGrid() {
    const fragment = document.createDocumentFragment();
    if (this.#cachedProductGrid) fragment.appendChild(this.#cachedProductGrid.cloneNode(true));

    return fragment;
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const request = (async () => {
      try {
        const sectionId = readSectionId();

        // Section render first when we know the id, full page otherwise (and as the fallback
        // for a product whose template does not carry the memoised section).
        let grid = sectionId
          ? await this.#requestProductGrid(withSectionId(productPageUrl, sectionId), controller.signal)
          : null;

        grid ??= await this.#requestProductGrid(productPageUrl, controller.signal, true);

        if (grid) this.#cachedProductGrid = document.importNode(grid, true);
      } catch (error) {
        if (error.name === 'AbortError') return;
        throw error;
      } finally {
        if (this.#abortController === controller) this.#abortController = null;
      }
    })();

    this.#pendingFetch = request.finally(() => {
      if (this.#pendingFetch === request) this.#pendingFetch = null;
    });

    return this.#pendingFetch;
  }

  /**
   * Fetches one URL and returns its product grid, or null when the response cannot supply one.
   * @param {string} url - The URL to fetch.
   * @param {AbortSignal} signal - Abort signal for the request.
   * @param {boolean} [learnSectionId] - Memoise the section id found in the response.
   * @returns {Promise<Element | null>}
   */
  async #requestProductGrid(url, signal, learnSectionId = false) {
    const response = await fetch(url, { signal });
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = html.querySelector('[data-product-grid-content]');
    if (!grid) return null;

    if (learnSectionId) {
      const sectionId =
        grid.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') ??
        grid.closest('[data-section-id]')?.getAttribute('data-section-id');

      if (sectionId) writeSectionId(sectionId);
    }

    return grid;
  }

  /**
   * Re-renders the variant picker.
   * @param {ParentNode} newHtml - A document or fragment holding the product grid.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  constructor() {
    super();

    // The modal is filled with a whole product section, and that markup carries refs of its
    // own - the media gallery ships <zoom-dialog><dialog ref="dialog">. Component#updateRefs
    // collects every [ref] in the subtree in document order and lets the last one win, so the
    // moment the gallery lands, this.refs.dialog stops pointing at this component's own
    // <dialog>: showDialog/closeDialog then act on the zoom dialog, which is closed, so
    // closeDialog returns at its `if (!dialog.open)` guard and the close button does nothing.
    //
    // Nested components are normally excluded by #isDescendant, but only once they are
    // upgraded - <zoom-dialog> is not a `*-component` tag, so before its module defines it,
    // getClosestComponent walks straight past it to here.
    //
    // Re-pin our own dialog on every refs rebuild. Every base-class read goes through this.
    let refs = this.refs;

    Object.defineProperty(this, 'refs', {
      configurable: true,
      get: () => refs,
      set: (value) => {
        const ownDialog = this.querySelector(':scope > dialog');

        refs = ownDialog instanceof HTMLDialogElement ? { ...value, dialog: ownDialog } : value;
      },
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
